import React from 'react'
import { Search, Filter } from 'lucide-react';
import PageHeader from '../../componentsII/PageHeader';
import api from '../../axios/api';
import { TableContainer, Switch, TableBody, TableCell, TableFooter, TableHead, TablePagination, TableRow, Table, Paper, IconButton, Dialog, DialogTitle, DialogContent, DialogContentText, DialogActions } from '@mui/material';
import { useEffect, useState } from 'react';
import { X, Trash2 as DeleteIcon, PenBoxIcon, PlusIcon } from 'lucide-react';
import CustomButton from '../../componentsII/Buttons';
import { useDispatch } from 'react-redux';
import { showNotification } from '../../redux/slices/notificationSlice';
import { TextField, FormControl, InputLabel, Select, MenuItem } from '@mui/material';
import debounce from 'lodash/debounce';
import { format } from "date-fns";
import AddUpdatePurchasedCoolingEmissionModal from '../../components/Masters/EmissionPurchasedCoolingMaster/AddUpdatePurchasedCoolingEmissionModal';

const EmissionPurchasedCoolingMaster = () => {
  const dispatch = useDispatch();

  const [isAddUpdatePurchasedCoolingEmissionModalOpen, setIsAddUpdatePurchasedCoolingEmissionModalOpen] = useState(false);
  const [selectedPurchasedCoolingEmission, setSelectedPurchasedCoolingEmission] = useState(null);

  const [purchasedCoolingEmission, setPurchasedCoolingEmission] = useState([]);
  const [totalPurchasedCoolingEmission, setTotalPurchasedCoolingEmission] = useState(0);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  // Enhanced filters state
  const [filters, setFilters] = useState({
    search: '',
    status: '',
    sortBy: 'created_date',
    sortOrder: 'ASC',
    year: '',
    unit: '',
    consumptionType: ''
  });

  // Filter options (These would be populated from API or defined statically)
  const [filterOptions, setFilterOptions] = useState({
    years: [],
    units: [],
    consumptionTypes: []
  });

  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [purchasedCoolingToDelete, setPurchasedCoolingToDelete] = useState(null);
  const [filterDialogOpen, setFilterDialogOpen] = useState(false);
  const [searchInput, setSearchInput] = useState('');

  // Get filter options separately from main data fetch
  const getFilterOptions = async () => {
    try {
      const response = await api.get('/admin/getPurchasedCoolingEmissionFilterOptions');

      if (response.data && response.data.status === "200") {
        setFilterOptions({
          years: response.data.years || [],
          units: response.data.units || [],
          consumptionTypes: response.data.consumptionTypes || []
        });
      }
    } catch (error) {
      console.error('Error fetching filter options:', error);

      // Fallback: Extract from current data if API call fails
      if (purchasedCoolingEmission.length > 0) {
        const uniqueYears = [...new Set(purchasedCoolingEmission.map(item => item.valid_year))];
        const uniqueUnits = [...new Set(purchasedCoolingEmission.map(item => item.unit_name))];
        const uniqueConsumptionTypes = [...new Set(purchasedCoolingEmission.map(item => item.consumption_type))];

        setFilterOptions({
          years: uniqueYears,
          units: uniqueUnits,
          consumptionTypes: uniqueConsumptionTypes
        });
      }
    }
  };

  const handleEdit = (emission) => {
    setSelectedPurchasedCoolingEmission(emission);
    setIsAddUpdatePurchasedCoolingEmissionModalOpen(true);
  };

  const getAllPurchasedCoolingEmission = async (pageNumber = page, limit = rowsPerPage, filterParams = filters) => {
    try {
      setIsLoading(true);
      const response = await api.get('/admin/getPurchasedCoolingEmission', {
        params: {
          page: pageNumber + 1,
          limit,
          search: filterParams.search,
          status: filterParams.status,
          sortBy: filterParams.sortBy,
          sortOrder: filterParams.sortOrder,
          year: filterParams.year,
          unit: filterParams.unit,
          consumptionType: filterParams.consumptionType
        }
      });
      setPurchasedCoolingEmission(response.data.data);
      setTotalPurchasedCoolingEmission(response.data.total);
    } catch (error) {
      dispatch(showNotification({
        message: 'Failed to fetch Purchased Cooling Emission',
        type: 'error',
      }));
    } finally {
      setIsLoading(false);
    }
  };

  const confirmDelete = () => {
    setIsSubmitting(true);

    api.delete(`/admin/deletePurchasedCoolingEmission/${purchasedCoolingToDelete.id}`)
      .then(() => {
        dispatch(showNotification({
          message: 'Purchased Cooling Emission deleted successfully',
          type: 'success',
        }));
        getAllPurchasedCoolingEmission();
      })
      .catch(() => {
        dispatch(showNotification({
          message: 'Failed to delete Purchased Cooling Emission factor',
          type: 'error',
        }));
      })
      .finally(() => {
        setDeleteDialogOpen(false);
        setIsSubmitting(false);
      });
  };

  const handleRemove = (emission) => {
    setPurchasedCoolingToDelete(emission);
    setDeleteDialogOpen(true);
  };

  // Handle search with debounce
  const debouncedSearch = React.useCallback(
    debounce((value) => {
      setFilters(prev => ({ ...prev, search: value }));
      setPage(0); // Reset to first page when searching
    }, 500),
    []
  );

  // Handle pagination
  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  // Handle sorting
  const handleSort = (field) => {
    setFilters(prev => ({
      ...prev,
      sortBy: field,
      sortOrder: prev.sortBy === field && prev.sortOrder === 'ASC' ? 'DESC' : 'ASC'
    }));
  };

  const handleActivateDeactivate = async (emission) => {
    try {
      setIsSubmitting(true);
      const response = await api.post(`/admin/activateDeactivatePurchasedCoolingEmission/${emission.id}`, {
        is_active: !emission.is_active
      });
      if (response.data.status == 200) {
        dispatch(showNotification({
          message: emission.is_active ? 'Purchased Cooling Emission Factor deactivated successfully' : 'Purchased Cooling Emission Factor activated successfully',
          type: "success"
        }));
        getAllPurchasedCoolingEmission();
      } else {
        dispatch(showNotification({
          message: emission.is_active ? 'Failed to deactivate Purchased Cooling Emission Factor' : 'Failed to activate Purchased Cooling Emission Factor',
          type: "error"
        }));
      }
    } catch (error) {
      console.error('Error activating/deactivating Purchased Cooling Emission Factor:', error);
      dispatch(showNotification({
        message: 'An error occurred',
        type: "error"
      }));
    } finally {
      setIsSubmitting(false);
    }
  };

  // Apply filters
  const applyFilters = () => {
    setPage(0); // Reset to first page when filters change
    getAllPurchasedCoolingEmission(0, rowsPerPage, filters);
    setFilterDialogOpen(false);
  };

  // Reset filters
  const resetFilters = () => {
    setFilters({
      search: '',
      status: '',
      sortBy: 'created_date',
      sortOrder: 'ASC',
      year: '',
      unit: '',
      consumptionType: ''
    });
    setPage(0);
    setFilterDialogOpen(false);
  };

  // Handle filter dialog open
  const handleOpenFilterDialog = () => {
    // Ensure filter options are loaded when dialog opens
    setFilterDialogOpen(true);
  };

  useEffect(() => {
    getAllPurchasedCoolingEmission();
    // Get filter options when component mounts
    getFilterOptions();
  }, [page, rowsPerPage, filters.sortBy, filters.sortOrder, filters.search, 
      filters.status, filters.year, filters.unit, filters.consumptionType]);

  // Get status filter label
  const getStatusLabel = (status) => {
    if (status === 'ACTIVE') return 'Active';
    if (status === 'INACTIVE') return 'Inactive';
    return 'All';
  };

  // Format consumption type for display
  const formatConsumptionType = (type) => {
    if (type === 'ONSITE_HEATSTEAM') return 'Onsite Heat/Steam';
    if (type === 'DISTRICT_HEATSTEAM') return 'District Heat/Steam';
    return type;
  };

  return (
    <>
      <PageHeader title="Purchased Cooling Emission">
        <div className="flex justify-end gap-5 items-center mt-5">
          <div className="flex items-center gap-4 flex-1">
            {/* Search Field */}
            <TextField
              size="small"
              placeholder="Search Purchased Cooling Emission..."
              value={searchInput}
              InputProps={{
                startAdornment: <Search className="w-5 h-5 text-gray-500 mr-2" />,
              }}
              onChange={(e) => {
                const value = e.target.value;
                setSearchInput(value)
                debouncedSearch(value); // Debounce the actual filter update
              }}
              className="min-w-[300px]"
            />

            {/* Filter Button */}
            <CustomButton
              className="bg-blue-600 hover:bg-blue-700 text-white"
              text={<><Filter size={18} /> Filters</>}
              click={handleOpenFilterDialog}
            />

            {/* Sort Dropdown */}
            <FormControl size="small" className="min-w-[200px]">
              <InputLabel>Sort By</InputLabel>
              <Select
                value={`${filters.sortBy}-${filters.sortOrder}`}
                label="Sort By"
                onChange={(e) => {
                  const [field, order] = e.target.value.split('-');
                  setFilters(prev => ({
                    ...prev,
                    sortBy: field,
                    sortOrder: order
                  }));
                }}
              >
                <MenuItem value="created_date-ASC">Date (Oldest First)</MenuItem>
                <MenuItem value="created_date-DESC">Date (Newest First)</MenuItem>
                <MenuItem value="emission_kgco2e-ASC">Emission (Low to High)</MenuItem>
                <MenuItem value="emission_kgco2e-DESC">Emission (High to Low)</MenuItem>
              </Select>
            </FormControl>
          </div>

          <CustomButton
            className="bg-green-600 hover:bg-green-700 text-white"
            text={<><PlusIcon /> Add Purchased Cooling Emission</>}
            click={() => setIsAddUpdatePurchasedCoolingEmissionModalOpen(true)}
          />
        </div>

        <TableContainer component={Paper} className="mt-4">
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Year</TableCell>
                <TableCell>Cooling Type</TableCell>
                <TableCell>Unit</TableCell>
                <TableCell>Emission KgCo2e</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Created Date</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {isLoading ? (
                <TableRow>
                  <TableCell colSpan={8} align="center">
                    Loading...
                  </TableCell>
                </TableRow>
              ) : purchasedCoolingEmission.length > 0 ? (
                purchasedCoolingEmission.map((emission) => (
                  <TableRow key={emission.id}>
                    <TableCell>{emission.valid_year}</TableCell>
                    <TableCell>{formatConsumptionType(emission.cooling_type)}</TableCell>
                    <TableCell>{emission.unit_name}</TableCell>
                    <TableCell>{emission.emission_kgco2e} <span className='font-bold'>kgco2e</span></TableCell>
                    <TableCell>
                      <Switch
                        checked={emission.is_active === 1}
                        color='success'
                        onChange={() => handleActivateDeactivate(emission)}
                        disabled={isSubmitting}
                      />
                    </TableCell>
                    <TableCell>
                      {emission.created_date ? format(new Date(emission.created_date), "dd-MM-yyyy") : ""}
                    </TableCell>
                    <TableCell sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                      <IconButton
                        size="small"
                        onClick={() => handleEdit(emission)}
                        sx={{
                          backgroundColor: '#16A34A',
                          color: 'white',
                          '&:hover': { backgroundColor: '#00bf46' },
                          p: '8px',
                        }}
                      >
                        <PenBoxIcon size={20} />
                      </IconButton>
                      <IconButton
                        size="small"
                        onClick={() => handleRemove(emission)}
                        sx={{
                          backgroundColor: '#df3e3e',
                          color: 'white',
                          '&:hover': { backgroundColor: '#ff0000' },
                          p: '8px',
                        }}
                      >
                        <DeleteIcon size={20} />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={8} align="center">
                    No records found
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
            <TableFooter>
              <TableRow>
                <TablePagination
                  rowsPerPageOptions={[5, 10, 25, 50]}
                  colSpan={8}
                  count={totalPurchasedCoolingEmission}
                  rowsPerPage={rowsPerPage}
                  page={page}
                  onPageChange={handleChangePage}
                  onRowsPerPageChange={handleChangeRowsPerPage}
                  labelDisplayedRows={({ from, to, count }) =>
                    `Page ${page + 1} of ${Math.ceil(count / rowsPerPage)} | ${from}-${to} of ${count}`
                  }
                />
              </TableRow>
            </TableFooter>
          </Table>
        </TableContainer>

        {/* Add/Edit Modal */}
        <Dialog
          open={isAddUpdatePurchasedCoolingEmissionModalOpen}
          onClose={() => { setIsAddUpdatePurchasedCoolingEmissionModalOpen(false); setSelectedPurchasedCoolingEmission(null) }}
          maxWidth="sm"
          fullWidth
        >
          <div className="flex justify-between items-start pt-2 px-2">
            <DialogTitle className="text-lg font-bold mt-1">
              {selectedPurchasedCoolingEmission ? "Edit Purchased Cooling Emission" : "Add New Purchased Cooling Emission"}
            </DialogTitle>
            <X
              className="w-6 h-6 cursor-pointer text-gray-500 hover:text-gray-700 transition"
              onClick={() => { setIsAddUpdatePurchasedCoolingEmissionModalOpen(false); setSelectedPurchasedCoolingEmission(null) }}
            />
          </div>
          <DialogContent>
            <AddUpdatePurchasedCoolingEmissionModal
              refetch={getAllPurchasedCoolingEmission}
              onClose={() => {
                setIsAddUpdatePurchasedCoolingEmissionModalOpen(false);
                setSelectedPurchasedCoolingEmission(null);
              }}
              initialData={selectedPurchasedCoolingEmission}
            />
          </DialogContent>
        </Dialog>

        {/* Delete Confirmation Dialog */}
        <Dialog
          open={deleteDialogOpen}
          onClose={() => setDeleteDialogOpen(false)}
        >
          <DialogTitle>Confirm Delete</DialogTitle>
          <DialogContent>
            <DialogContentText>
              Are you sure you want to delete the heat steam emission for year {purchasedCoolingToDelete?.valid_year}? This action cannot be undone.
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <div className="flex justify-end gap-5 items-center mt-5">
              <CustomButton
                className="bg-gray-500 hover:bg-gray-700 text-white"
                text="Cancel"
                disabled={isSubmitting}
                click={() => setDeleteDialogOpen(false)}
              />
              <CustomButton
                className="bg-red-600 hover:bg-red-700 text-white"
                click={confirmDelete}
                text='Delete'
                disabled={isSubmitting}
              />
            </div>
          </DialogActions>
        </Dialog>

        {/* Filters Dialog */}
        <Dialog
          open={filterDialogOpen}
          onClose={() => setFilterDialogOpen(false)}
          maxWidth="sm"
          fullWidth
        >
          <div className="flex justify-between items-start pt-2 px-2">
            <DialogTitle className="text-lg font-bold mt-1">
              Filter Purchased Cooling Emissions
            </DialogTitle>
            <X
              className="w-6 h-6 cursor-pointer text-gray-500 hover:text-gray-700 transition"
              onClick={() => setFilterDialogOpen(false)}
            />
          </div>
          <DialogContent>
            <div className="space-y-4 py-2">
              {/* Year Filter */}
              <FormControl fullWidth size="small">
                <InputLabel>Year</InputLabel>
                <Select
                  value={filters.year}
                  label="Year"
                  onChange={(e) => setFilters(prev => ({ ...prev, year: e.target.value }))}
                >
                  <MenuItem value="">All Years</MenuItem>
                  {filterOptions.years.map(year => (
                    <MenuItem key={year} value={year}>{year}</MenuItem>
                  ))}
                </Select>
              </FormControl>

              {/* Consumption Type Filter */}
              <FormControl fullWidth size="small">
                <InputLabel>Consumption Type</InputLabel>
                <Select
                  value={filters.consumptionType}
                  label="Consumption Type"
                  onChange={(e) => setFilters(prev => ({ ...prev, consumptionType: e.target.value }))}
                >
                  <MenuItem value="">All Consumption Types</MenuItem>
                  {filterOptions.consumptionTypes.map(type => (
                    <MenuItem key={type} value={type}>{formatConsumptionType(type)}</MenuItem>
                  ))}
                </Select>
              </FormControl>

              {/* Unit Filter */}
              <FormControl fullWidth size="small">
                <InputLabel>Unit</InputLabel>
                <Select
                  value={filters.unit}
                  label="Unit"
                  onChange={(e) => setFilters(prev => ({ ...prev, unit: e.target.value }))}
                >
                  <MenuItem value="">All Units</MenuItem>
                  {filterOptions.units.map(unit => (
                    <MenuItem key={unit} value={unit}>{unit}</MenuItem>
                  ))}
                </Select>
              </FormControl>

              {/* Status Filter */}
              <FormControl fullWidth size="small">
                <InputLabel>Status</InputLabel>
                <Select
                  value={filters.status}
                  label="Status"
                  onChange={(e) => setFilters(prev => ({ ...prev, status: e.target.value }))}
                >
                  <MenuItem value="">All</MenuItem>
                  <MenuItem value="ACTIVE">Active</MenuItem>
                  <MenuItem value="INACTIVE">Inactive</MenuItem>
                </Select>
              </FormControl>
            </div>
          </DialogContent>
          <DialogActions>
            <div className="flex justify-end gap-5 items-center mt-5 p-2">
              <CustomButton
                className="bg-gray-500 hover:bg-gray-700 text-white"
                text="Reset"
                click={resetFilters}
              />
              <CustomButton
                className="bg-blue-600 hover:bg-blue-700 text-white"
                text="Apply Filters"
                click={applyFilters}
              />
            </div>
          </DialogActions>
        </Dialog>
      </PageHeader>
    </>
  )
}

export default EmissionPurchasedCoolingMaster